import {
  RefObject, useCallback, useEffect, useMemo, useRef,
} from 'react';

import useWindow from 'app/hooks/useWindow';

// @todo need to fix for ref also need to add type guards
const useResizeObserver = (
  handleObserverEvent: (entry: ResizeObserverEntry) => void,
  target?: Window | Element | RefObject<Element> | string,
): void => {
  const observerRef = useRef<ResizeObserver>();
  const window = useWindow();

  const targetFinal = useMemo(() => {
    if (!window) {
      return undefined;
    }
    if (!target) {
      return window;
    }
    if (typeof target === 'string') {
      return document.querySelector(target);
    }
    if (target instanceof Element || target instanceof Window) {
      return target;
    }
    return target.current;
  }, [window, target]);

  const handleEvent = useCallback(
    (event: any) => {
      if (targetFinal instanceof Window) {
        handleObserverEvent?.({
          devicePixelContentBoxSize: [],
          borderBoxSize: [
            {
              blockSize: targetFinal.innerWidth,
              inlineSize: targetFinal.innerHeight,
            },
          ],
          contentBoxSize: [
            {
              blockSize: targetFinal.innerWidth,
              inlineSize: targetFinal.innerHeight,
            },
          ],
          contentRect: {
            width: targetFinal.innerWidth,
            height: targetFinal.innerHeight,
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            x: 0,
            y: 0,
            toJSON(): string {
              return JSON.stringify(this);
            },
          },
          target: targetFinal as unknown as Element,
        } as ResizeObserverEntry);
      }
      if (targetFinal instanceof Element) {
        handleObserverEvent?.(event[0]);
      }
    },
    [handleObserverEvent, targetFinal],
  );

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return () => undefined;
    }
    if (targetFinal instanceof Window) {
      window?.addEventListener('resize', handleEvent);
      handleEvent([]);
      return () => {
        window?.removeEventListener('resize', handleEvent);
      };
    }
    if (targetFinal instanceof Element) {
      observerRef.current = new ResizeObserver(handleEvent);
      observerRef.current?.observe(targetFinal);
      return () => {
        observerRef.current?.unobserve(targetFinal);
        observerRef.current = undefined;
      };
    }
    return () => undefined;
  }, [handleEvent, targetFinal, window]);
};

export default useResizeObserver;
